/* eslint-disable react/prop-types */
import { Upload, UploadFile, UploadProps } from 'antd';
import { RcFile } from 'antd/es/upload';
import {
  UploadRequestMethod,
  UploadRequestOption as RcCustomRequestOptions,
} from 'rc-upload/lib/interface';
import React, { FC, useState } from 'react';
import { FaInbox } from 'react-icons/fa';

import CustomButton from '../CustomButton';
import Loader from '../Loader';
import Typography from '../Typography';
import {
  StyledFileUploader,
  StyledFileUploaderContainer,
} from './FileUploader.styled';
import { ALLOWED_CONTENT_TYPES } from './utils';

type Props = {
  props: UploadProps;
  hasFile: boolean;
  onUploadFile: (info: RcCustomRequestOptions) => Promise<void>;
  onResetAfterUpload?: () => void;
  hideUploadListWhileUploading?: boolean;
  hideNativeListAfterUploadPress?: boolean;
};

enum FileUploaderActions {
  Upload = 'Subir archivo',
  Select = 'Selecciona un archivo',
}

enum FileUploadPayload {
  Method = 'post',
  Action = 'upload',
}

const FileUploader: FC<Props> = ({ props, hasFile, onUploadFile, onResetAfterUpload, hideUploadListWhileUploading, hideNativeListAfterUploadPress }) => {
  const uploadChildrenRef = React.createRef<HTMLDivElement>();
  const uploadContainerRef = React.createRef<{ fileList: UploadFile[] }>();

  const [isFileUploading, setIsFileUploading] = useState<boolean>(false);
  const [hasUserPressedUpload, setHasUserPressedUpload] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<UploadFile | null>(null);

  const handleOnSelectFile = () => {
    uploadChildrenRef.current?.click();
  };

  const handleOnUploadFile = () => {
    setIsFileUploading(true);
    setHasUserPressedUpload(true);
    const file = selectedFile;
    if (!file || !file.originFileObj) {
      setIsFileUploading(false);
      return;
    }

    const newFileUploadPayload = {
      file: file.originFileObj as RcFile,
      action: FileUploadPayload.Action,
      method: FileUploadPayload.Method as UploadRequestMethod,
    };
    onUploadFile(newFileUploadPayload)
      .finally(() => {
        // limpiar selección local tras subir
        if (uploadContainerRef.current) {
          uploadContainerRef.current.fileList = [] as unknown as UploadFile[];
        }
        setSelectedFile(null);
        if (onResetAfterUpload) onResetAfterUpload();
        setIsFileUploading(false);
      });
  };

  // Si el usuario vuelve a seleccionar un archivo, permitimos ver la lista nativa nuevamente
  React.useEffect(() => {
    if (hasFile) {
      setHasUserPressedUpload(false);
    }
  }, [hasFile]);

  const handleInternalOnChange = (info: any) => {
    try {
      const first = Array.isArray(info?.fileList) ? info.fileList[0] : null;
      setSelectedFile(first || null);
    } catch (_) {
      setSelectedFile(null);
    }
    if (props.onChange) props.onChange(info);
  };

  const fileUploadActionContent = !hasFile
    ? FileUploaderActions.Select
    : FileUploaderActions.Upload;

  const handleUploadAction = React.useCallback(
    !hasFile ? handleOnSelectFile : handleOnUploadFile,
    [hasFile],
  );

  return (
    <StyledFileUploaderContainer>
      {isFileUploading && <Loader />}
      <Upload
        {...props}
        onChange={handleInternalOnChange}
        ref={uploadContainerRef}
        accept={ALLOWED_CONTENT_TYPES.join()}
        showUploadList={
          props.showUploadList !== undefined
            ? props.showUploadList
            : hideNativeListAfterUploadPress
            ? !hasUserPressedUpload
            : hideUploadListWhileUploading
            ? !isFileUploading
            : undefined
        }
      >
        <StyledFileUploader ref={uploadChildrenRef}>
          <>
            <FaInbox size="5rem" />
            <Typography theme="p">Arrastre un archivo para subir</Typography>
          </>
        </StyledFileUploader>
      </Upload>

      <CustomButton
        text={fileUploadActionContent}
        $theme="primary"
        $isMini
        $marginTop={hasFile ? '1rem' : 'unset'}
        disabled={isFileUploading}
        onPress={handleUploadAction}
      />
    </StyledFileUploaderContainer>
  );
};

export default FileUploader;
